import { useState } from "react";
import {
  ActionIcon,
  Badge,
  Button,
  Drawer,
  Group,
  Menu,
  Stack,
  Text,
  TextInput,
  Textarea,
  Title,
} from "@mantine/core";
import { useForm } from "@mantine/form";
import { notifications } from "@mantine/notifications";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  IconDots,
  IconPencil,
  IconPlus,
  IconStar,
  IconTrash,
} from "@tabler/icons-react";
import { FormModal } from "@/components/FormModal";
import { ConfirmDialog } from "@/components/ConfirmDialog";
import { Can } from "@/components/Can";
import { api } from "@/lib/api";
import { formatAPIError, qk } from "@/lib/apiHelpers";
import type { Client, Contact, ContactCreateRequest, ContactUpdateRequest } from "@/types/api";

interface ContactFormValues {
  full_name: string;
  position: string;
  phone: string;
  mobile: string;
  email: string;
  notes: string;
}

function ContactForm({
  initial,
  form,
}: {
  initial?: Contact;
  form: ReturnType<typeof useForm<ContactFormValues>>;
}) {
  void initial; // used only to initialize form externally
  return (
    <Stack gap="md">
      <TextInput label="ФИО" required {...form.getInputProps("full_name")} />
      <TextInput label="Должность" {...form.getInputProps("position")} />
      <Group grow>
        <TextInput label="Телефон" {...form.getInputProps("phone")} />
        <TextInput label="Мобильный" {...form.getInputProps("mobile")} />
      </Group>
      <TextInput label="Email" {...form.getInputProps("email")} />
      <Textarea label="Примечания" {...form.getInputProps("notes")} autosize minRows={2} />
    </Stack>
  );
}

export function ClientContacts({
  client,
  opened,
  onClose,
}: {
  client: Client;
  opened: boolean;
  onClose: () => void;
}) {
  const qc = useQueryClient();

  const { data: contactsRaw, isLoading } = useQuery({
    queryKey: qk.clientContacts(client.id),
    queryFn: async () =>
      (await api.get<Contact[] | null>(`/api/v1/clients/${client.id}/contacts`)).data,
    enabled: opened,
  });
  const contacts = contactsRaw ?? [];

  const [editing, setEditing] = useState<Contact | null | undefined>(undefined);
  const [confirmDelete, setConfirmDelete] = useState<Contact | null>(null);

  const contactForm = useForm<ContactFormValues>({
    initialValues: { full_name: "", position: "", phone: "", mobile: "", email: "", notes: "" },
    validate: {
      full_name: (v) => (v.trim() ? null : "Обязательно"),
      email: (v) => (!v || /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v) ? null : "Некорректный email"),
    },
  });

  const openCreate = () => {
    contactForm.reset();
    setEditing(null);
  };

  const openEdit = (c: Contact) => {
    contactForm.setValues({
      full_name: c.full_name,
      position: c.position ?? "",
      phone: c.phone ?? "",
      mobile: c.mobile ?? "",
      email: c.email ?? "",
      notes: c.notes ?? "",
    });
    setEditing(c);
  };

  const invalidate = () => void qc.invalidateQueries({ queryKey: ["clientContacts", client.id] });

  const createMut = useMutation({
    mutationFn: async (body: ContactCreateRequest) =>
      (await api.post<Contact>(`/api/v1/clients/${client.id}/contacts`, body)).data,
    onSuccess: () => {
      notifications.show({ message: "Контакт добавлен", color: "green" });
      invalidate();
      setEditing(undefined);
    },
    onError: (e) => notifications.show({ title: "Ошибка", message: formatAPIError(e), color: "red" }),
  });

  const updateMut = useMutation({
    mutationFn: async ({ cid, body }: { cid: string; body: ContactUpdateRequest }) =>
      (await api.patch<Contact>(`/api/v1/clients/${client.id}/contacts/${cid}`, body)).data,
    onSuccess: () => {
      notifications.show({ message: "Контакт обновлён", color: "green" });
      invalidate();
      setEditing(undefined);
    },
    onError: (e) => notifications.show({ title: "Ошибка", message: formatAPIError(e), color: "red" }),
  });

  const deleteMut = useMutation({
    mutationFn: async (cid: string) =>
      (await api.delete(`/api/v1/clients/${client.id}/contacts/${cid}`)).data,
    onSuccess: () => {
      notifications.show({ message: "Контакт удалён", color: "green" });
      invalidate();
      setConfirmDelete(null);
    },
    onError: (e) => notifications.show({ title: "Ошибка", message: formatAPIError(e), color: "red" }),
  });

  const primaryMut = useMutation({
    mutationFn: async (cid: string) =>
      (await api.post(`/api/v1/clients/${client.id}/contacts/${cid}/primary`)).data,
    onSuccess: () => {
      notifications.show({ message: "Основной контакт установлен", color: "green" });
      invalidate();
    },
    onError: (e) => notifications.show({ title: "Ошибка", message: formatAPIError(e), color: "red" }),
  });

  const submit = () => {
    if (contactForm.validate().hasErrors) return;
    const v = contactForm.values;
    const body: ContactCreateRequest = {
      full_name: v.full_name,
      position: v.position || undefined,
      phone: v.phone || undefined,
      mobile: v.mobile || undefined,
      email: v.email || undefined,
      notes: v.notes || undefined,
    };
    if (editing) {
      updateMut.mutate({ cid: editing.id, body });
    } else {
      createMut.mutate(body);
    }
  };

  return (
    <Drawer
      opened={opened}
      onClose={onClose}
      title={
        <div>
          <Text fw={600}>Контакты</Text>
          <Text size="sm" c="dimmed">{client.name}</Text>
        </div>
      }
      position="right"
      size="lg"
    >
      <Stack gap="md">
        <Group justify="space-between">
          <Title order={4}>Список контактов</Title>
          <Can perm="clients:write">
            <Button size="xs" leftSection={<IconPlus size={14} />} onClick={openCreate}>
              Добавить
            </Button>
          </Can>
        </Group>

        {isLoading && <Text c="dimmed" size="sm">Загрузка...</Text>}

        {!isLoading && contacts.length === 0 && (
          <Text c="dimmed" size="sm">Контакты не добавлены</Text>
        )}

        {contacts.map((c) => (
          <Stack key={c.id} gap={4} style={{ borderBottom: "1px solid var(--mantine-color-gray-2)", paddingBottom: 12 }}>
            <Group justify="space-between" wrap="nowrap">
              <Group gap="xs" wrap="nowrap">
                <Text fw={500}>{c.full_name}</Text>
                {c.is_primary && <Badge color="blue" size="xs">Основной</Badge>}
              </Group>
              <Can perm="clients:write">
                <Menu position="bottom-end" shadow="md">
                  <Menu.Target>
                    <ActionIcon variant="subtle" size="sm">
                      <IconDots size={14} />
                    </ActionIcon>
                  </Menu.Target>
                  <Menu.Dropdown>
                    <Menu.Item leftSection={<IconPencil size={14} />} onClick={() => openEdit(c)}>
                      Редактировать
                    </Menu.Item>
                    {!c.is_primary && (
                      <Menu.Item
                        leftSection={<IconStar size={14} />}
                        onClick={() => primaryMut.mutate(c.id)}
                      >
                        Сделать основным
                      </Menu.Item>
                    )}
                    <Menu.Item
                      leftSection={<IconTrash size={14} />}
                      color="red"
                      onClick={() => setConfirmDelete(c)}
                    >
                      Удалить
                    </Menu.Item>
                  </Menu.Dropdown>
                </Menu>
              </Can>
            </Group>
            {c.position && <Text size="sm" c="dimmed">{c.position}</Text>}
            <Group gap="md">
              {c.phone && <Text size="sm">{c.phone}</Text>}
              {c.mobile && <Text size="sm">{c.mobile}</Text>}
              {c.email && <Text size="sm">{c.email}</Text>}
            </Group>
            {c.notes && <Text size="sm" c="dimmed">{c.notes}</Text>}
          </Stack>
        ))}
      </Stack>

      <FormModal
        opened={editing !== undefined}
        onClose={() => setEditing(undefined)}
        title={editing ? "Редактировать контакт" : "Новый контакт"}
        onSubmit={submit}
        loading={createMut.isPending || updateMut.isPending}
      >
        <ContactForm initial={editing ?? undefined} form={contactForm} />
      </FormModal>

      <ConfirmDialog
        opened={!!confirmDelete}
        onClose={() => setConfirmDelete(null)}
        onConfirm={() => confirmDelete && deleteMut.mutate(confirmDelete.id)}
        title="Удалить контакт?"
        message={`Контакт "${confirmDelete?.full_name}" будет удалён.`}
        loading={deleteMut.isPending}
      />
    </Drawer>
  );
}
